/* USAGE
Example:
  <KSlider>Children go here </KSlider>

Props:
  "className" - Allow additional classes to be passed to the component - OPTIONAL
*/

import React, { useCallback, useRef, useState, useEffect } from "react";
import WIconButton from "../wicon-button/WIconButton";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";
interface Props {
  children: React.ReactNode;
  className?: string;
  isLoading?: boolean;
}

export const WSlider = ({ children, ...props }: Props) => {
  const slider = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [hasMoreContent, setHasMoreContent] = useState(true);

  // Add function to check if there's more content to scroll
  const checkMoreContent = useCallback(() => {
    if (slider.current) {
      const { scrollLeft, scrollWidth, clientWidth } = slider.current;
      setHasMoreContent(scrollLeft + clientWidth < scrollWidth);
    }
  }, []);

  const scroll = useCallback(
    (direction: string) => {
      if (slider.current) {
        // Calculate how many cards are fully visible
        const containerWidth = slider.current.clientWidth;
        const cardWidth = slider.current.children[0]?.clientWidth || 0;
        const gap = 16; // This is the gap-4 in pixels
        const fullyVisibleCards =
          Math.floor(containerWidth / (cardWidth + gap)) - 1;
        const scrollAmount = fullyVisibleCards * (cardWidth + gap);

        if (direction === "right") {
          slider.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
          setPage((prevState) => prevState + 1);
        } else {
          // Get the current scroll position
          const currentScroll = slider.current.scrollLeft;
          // Calculate the nearest left multiple of (cardWidth + gap)
          const targetScroll =
            Math.floor(currentScroll / (cardWidth + gap)) * (cardWidth + gap);

          if (currentScroll <= scrollAmount) {
            // If we're close to the start, go to the beginning
            slider.current.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            // Scroll to the previous set of cards
            slider.current.scrollTo({
              left: targetScroll - scrollAmount,
              behavior: "smooth",
            });
          }
          setPage((prevState) => prevState - 1);
        }

        // Check for more content after scroll
        setTimeout(checkMoreContent, 100);
      }
    },
    [checkMoreContent]
  );

  // Add scroll event listener to update hasMoreContent state
  useEffect(() => {
    const currentSlider = slider.current;
    checkMoreContent();

    currentSlider?.addEventListener("scroll", checkMoreContent);
    window.addEventListener("resize", checkMoreContent);

    return () => {
      currentSlider?.removeEventListener("scroll", checkMoreContent);
      window.removeEventListener("resize", checkMoreContent);
    };
  }, [checkMoreContent]);

  return (
    <div
      className={`relative flex flex-grow-0 flex-shrink-0  ${
        props.className ? props.className : ""
      }`}
    >
      {/* Slider children */}
      <div
        ref={slider}
        className="flex gap-2 pb-0 pr-5 -mr-5 overflow-hidden overflow-x-auto lg:mr-0 lg:overflow-x-hidden lg:pr-9 lg:gap-4 lg:scroll-smooth snap-x snap-mandatory force-scroll-x"
      >
        {children}
      </div>
      {/* Slider prev/next buttons on desktop */}

      <div className="absolute top-0 items-center justify-between hidden w-full h-full pointer-events-none lg:flex z-[110]">
        {page > 1 && (
          <div className="flex items-center h-full px-5 bg-gradient-to-r from-white/75">
            <WIconButton
              icon={faArrowLeft}
              type="white"
              className="border-0 pointer-events-auto text-ekblue"
              onClick={() => scroll("left")}
            />
          </div>
        )}

        <div className="h-full px-5" />

        {hasMoreContent && (
          <div className="flex items-center h-full px-5 bg-gradient-to-l from-white/75">
            <WIconButton
              icon={faArrowRight}
              type="white"
              className="border-0 pointer-events-auto text-ekblue"
              onClick={() => scroll("right")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WSlider;
